import { useState, type ReactNode } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { GitBranch, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'

interface VersionSelectorProps {
  clipId: Id<"workflowClips">
  versionCount: number
  className?: string
  /** Custom trigger content. When provided, `className` styles the trigger button entirely. */
  children?: ReactNode
}

export function VersionSelector({ clipId, versionCount, className, children }: VersionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)

  // Only fetch versions when dropdown is open
  const versions = useQuery(
    api.workflowHistory.getClipVersions,
    isOpen ? { clipId } : "skip"
  )

  const switchVersion = useMutation(api.workflowHistory.switchClipVersion)

  const handleSwitch = async (videoId: Id<"videos">) => {
    setSwitching(videoId)
    try {
      await switchVersion({ clipId, videoId })
    } finally {
      setSwitching(null)
      setIsOpen(false)
    }
  }

  if (versionCount <= 1) return null

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={
            children
              ? className
              : cn(
                  "absolute top-1 right-1 z-10 bg-black/40 hover:bg-black/60 text-white/80 text-[8px] font-medium px-1 py-0.5 rounded flex items-center gap-0.5 transition-colors",
                  className
                )
          }
          title={`${versionCount} versions available - click to switch`}
        >
          {children ?? (
            <>
              <GitBranch size={8} />
              <span>v{versionCount}</span>
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 max-h-80 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          Video Versions ({versionCount})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {!versions ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={16} className="animate-spin text-muted-foreground" />
          </div>
        ) : versions.versions.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2 px-2">
            No versions available
          </div>
        ) : (
          versions.versions.map((version) => (
            <DropdownMenuItem
              key={version.videoId}
              onClick={() => handleSwitch(version.videoId as Id<"videos">)}
              disabled={switching !== null}
              className="flex items-start gap-2 p-2 cursor-pointer"
            >
              {/* Video thumbnail */}
              <div className="w-16 h-9 bg-muted rounded overflow-hidden shrink-0">
                {version.thumbnailUrl ? (
                  <img
                    src={version.thumbnailUrl}
                    alt={`Version ${version.versionNumber}`}
                    className="w-full h-full object-cover"
                  />
                ) : version.videoUrl ? (
                  <video
                    src={version.videoUrl}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <GitBranch size={12} className="text-border" />
                  </div>
                )}
              </div>

              {/* Version info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">
                    Version {version.versionNumber}
                  </span>
                  {version.isActive && (
                    <span className="bg-success/10 text-success text-[9px] px-1 rounded">
                      Active
                    </span>
                  )}
                  {switching === version.videoId && (
                    <Loader2 size={10} className="animate-spin text-purple-600" />
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {new Date(version.createdAt).toLocaleDateString()} {new Date(version.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className={`text-[9px] ${
                  version.status === 'succeeded' ? 'text-success' :
                  version.status === 'failed' ? 'text-destructive' :
                  'text-muted-foreground'
                }`}>
                  {version.status}
                </div>
              </div>

              {/* Active indicator */}
              {version.isActive && (
                <Check size={14} className="text-success shrink-0" />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
